/**
 * Portfolio table column definitions + the small cell components they render.
 *
 * The columns surface a sensible cross-section of every projection table — the
 * loan identity (with a tax-id indicator), loan-level attributes (amount, date,
 * FICO, rate), the observation summary (current balance, snapshot count + latest
 * period), the transaction summary (count + summed flow), and per-loan modeling
 * readiness as three pips. Sortable columns carry an `id` equal to their server
 * `PortfolioSortKey`, so the table's sorting state maps straight to the query.
 */
import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import type {
  PortfolioLoanRow,
  PortfolioLoanReadiness,
  PortfolioOriginalBalanceSource,
  PortfolioTerminalStatus,
  PortfolioTermSource,
  InferredType,
  NumericSubtype,
} from '@repo/shared/common';
import { Tooltip } from '~/components/ui';
import { ShieldCheck } from '~/components/icons';
import { formatFieldValue } from '~/lib/format-field-value';
import {
  EMPTY_VALUE,
  formatCurrency,
  formatInteger,
  formatRawDate,
  shortenLoanId,
} from './format';
import styles from './columns.module.css';

function cx(...classes: (string | false | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

/** Render a formatted value, dimming the neutral placeholder when it's absent. */
function valueOrEmpty(formatted: string): ReactNode {
  return formatted === EMPTY_VALUE ? <span className={styles.empty}>{EMPTY_VALUE}</span> : formatted;
}

/**
 * Bridge {@link formatFieldValue} (the shared, type-aware formatter) to this
 * table's empty-placeholder convention: the shared formatter renders an absent
 * value as '', which we map to the dimmed `EMPTY_VALUE` so the cell reads as
 * "no data" rather than blank.
 */
function formatLoanField(
  value: string | null,
  valueType: InferredType,
  valueSubtype: NumericSubtype | null,
): string {
  const formatted = formatFieldValue(value, valueType, valueSubtype);
  return formatted === '' ? EMPTY_VALUE : formatted;
}

function LoanCell({ loanId, hasTaxId }: { readonly loanId: string; readonly hasTaxId: boolean }): ReactNode {
  const { t } = useTranslation();
  return (
    <span className={styles.loanCell} title={loanId}>
      <span className={styles.loanId}>{shortenLoanId(loanId)}</span>
      {hasTaxId ? (
        <Tooltip label={t('portfolio.columns.taxIdOnFile')} withArrow position="top">
          <span className={styles.taxBadge} aria-label={t('portfolio.columns.taxIdOnFile')}>
            <ShieldCheck size={13} aria-hidden />
          </span>
        </Tooltip>
      ) : null}
    </span>
  );
}

function StackedCell({
  primary,
  secondary,
}: {
  readonly primary: string;
  readonly secondary?: string | undefined;
}): ReactNode {
  return (
    <span className={styles.stacked}>
      <span className={styles.stackedPrimary}>{primary}</span>
      {secondary !== undefined && secondary !== '' ? (
        <span className={styles.stackedSecondary}>{secondary}</span>
      ) : null}
    </span>
  );
}

/** The loan's terminal state (D2). Renders "Active" for the common `none` case. */
function TerminalStatusCell({ status }: { readonly status: PortfolioTerminalStatus }): ReactNode {
  const { t } = useTranslation();
  return <span>{t(`portfolio.terminalStatus.${status}`)}</span>;
}

/** F184: the Original Balance with its provenance (provided vs estimated) as a subtitle —
 *  mirrors {@link TermCell}. No subtitle when there is no balance to attribute. */
function OriginalBalanceCell({
  balance,
  source,
}: {
  readonly balance: string | null;
  readonly source: PortfolioOriginalBalanceSource;
}): ReactNode {
  const { t } = useTranslation();
  const formatted = formatCurrency(balance);
  if (formatted === EMPTY_VALUE) return valueOrEmpty(formatted);
  return (
    <StackedCell
      primary={formatted}
      secondary={t(`portfolio.originalBalanceSource.${source}`)}
    />
  );
}

/** Effective term in months with its provenance (provided / derived / none) as a subtitle. */
function TermCell({
  months,
  source,
}: {
  readonly months: number | null;
  readonly source: PortfolioTermSource;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <StackedCell
      primary={months === null ? EMPTY_VALUE : t('portfolio.months', { count: months })}
      secondary={t(`portfolio.termSource.${source}`)}
    />
  );
}

const READINESS_OUTPUTS = [
  { key: 'creditLoss', labelKey: 'portfolio.readiness.creditLoss' },
  { key: 'payoff', labelKey: 'portfolio.readiness.payoff' },
  { key: 'prepayment', labelKey: 'portfolio.readiness.prepayment' },
] as const satisfies ReadonlyArray<{ key: keyof PortfolioLoanReadiness; labelKey: string }>;

function ReadinessPips({ readiness }: { readonly readiness: PortfolioLoanReadiness }): ReactNode {
  const { t } = useTranslation();
  return (
    <span className={styles.pips}>
      {READINESS_OUTPUTS.map(({ key, labelKey }) => {
        const ready = readiness[key];
        const state = ready ? t('portfolio.readiness.ready') : t('portfolio.readiness.notReady');
        const label = `${t(labelKey)}: ${state}`;
        return (
          <Tooltip key={key} label={label} withArrow position="top">
            <span
              className={cx(styles.pip, ready ? styles.pipReady : styles.pipMuted)}
              role="img"
              aria-label={label}
            />
          </Tooltip>
        );
      })}
    </span>
  );
}

/** A compact legend explaining the readiness pips (fill = ready) and their column order. */
export function ReadinessLegend(): ReactNode {
  const { t } = useTranslation();
  return (
    <span className={styles.legend} data-testid="portfolio-readiness-legend">
      <span className={styles.legendItem}>
        <span className={cx(styles.pip, styles.pipReady)} aria-hidden />
        {t('portfolio.readiness.ready')}
      </span>
      <span className={styles.legendItem}>
        <span className={cx(styles.pip, styles.pipMuted)} aria-hidden />
        {t('portfolio.readiness.notReady')}
      </span>
      <span className={styles.legendOrder}>{t('portfolio.readiness.order')}</span>
    </span>
  );
}

/** Build the portfolio table columns (memoized; re-derives only when the locale changes). */
export function usePortfolioColumns(): ColumnDef<PortfolioLoanRow>[] {
  const { t } = useTranslation();
  return useMemo<ColumnDef<PortfolioLoanRow>[]>(
    () => [
      {
        id: 'loan_id',
        accessorKey: 'loanId',
        header: () => t('portfolio.columns.loan'),
        cell: ({ row }) => <LoanCell loanId={row.original.loanId} hasTaxId={row.original.hasTaxId} />,
        size: 230,
        enableSorting: true,
        meta: { grow: true },
      },
      {
        id: 'original_amount',
        accessorKey: 'originalLoanAmount',
        header: () => t('portfolio.columns.originalAmount'),
        cell: ({ row }) => valueOrEmpty(formatCurrency(row.original.originalLoanAmount)),
        size: 140,
        enableSorting: true,
        meta: { align: 'right', mono: true },
      },
      {
        id: 'origination_date',
        accessorKey: 'originationDate',
        header: () => t('portfolio.columns.origination'),
        cell: ({ row }) => valueOrEmpty(formatRawDate(row.original.originationDate)),
        size: 130,
        enableSorting: false,
        meta: { mono: true },
      },
      {
        id: 'fico',
        accessorKey: 'ficoScore',
        header: () => t('portfolio.columns.fico'),
        cell: ({ row }) => valueOrEmpty(formatInteger(row.original.ficoScore)),
        size: 96,
        enableSorting: true,
        meta: { align: 'right', mono: true },
      },
      {
        id: 'interest_rate',
        accessorKey: 'interestRate',
        header: () => t('portfolio.columns.rate'),
        // Rates are plain percent-points (catalog decision), so a `number` field:
        // 5.25 reads as "5.25", not "525%". Routed through the shared type-aware
        // formatter (equivalent to the prior grouped-decimal display).
        cell: ({ row }) => valueOrEmpty(formatLoanField(row.original.interestRate, 'numeric', 'number')),
        size: 96,
        enableSorting: true,
        meta: { align: 'right', mono: true },
      },
      {
        id: 'current_balance',
        accessorKey: 'currentBalance',
        header: () => t('portfolio.columns.currentBalance'),
        cell: ({ row }) => valueOrEmpty(formatCurrency(row.original.currentBalance)),
        size: 150,
        enableSorting: true,
        meta: { align: 'right', mono: true },
      },
      {
        id: 'original_balance',
        accessorKey: 'originalBalance',
        header: () => t('portfolio.columns.originalBalance'),
        cell: ({ row }) => (
          <OriginalBalanceCell
            balance={row.original.originalBalance}
            source={row.original.originalBalanceSource}
          />
        ),
        size: 140,
        enableSorting: false,
        meta: { align: 'right', mono: true },
      },
      {
        id: 'status',
        accessorKey: 'terminalStatus',
        header: () => t('portfolio.columns.status'),
        cell: ({ row }) => (
          <TerminalStatusCell status={row.original.terminalStatus} />
        ),
        size: 120,
        enableSorting: false,
      },
      {
        id: 'term',
        accessorKey: 'effectiveTermMonths',
        header: () => t('portfolio.columns.term'),
        cell: ({ row }) => (
          <TermCell
            months={row.original.effectiveTermMonths}
            source={row.original.termSource}
          />
        ),
        size: 120,
        enableSorting: false,
        meta: { align: 'right' },
      },
      {
        id: 'age',
        accessorKey: 'ageMonths',
        header: () => t('portfolio.columns.age'),
        cell: ({ row }) =>
          row.original.ageMonths === null
            ? valueOrEmpty(EMPTY_VALUE)
            : t('portfolio.months', { count: row.original.ageMonths }),
        size: 96,
        enableSorting: false,
        meta: { align: 'right', mono: true },
      },
      {
        id: 'modelable',
        accessorKey: 'isModeled',
        header: () => t('portfolio.columns.modelable'),
        cell: ({ row }) =>
          t(row.original.isModeled ? 'portfolio.modelable.yes' : 'portfolio.modelable.no'),
        size: 100,
        enableSorting: false,
        meta: { align: 'center' },
      },
      {
        id: 'snapshots',
        accessorKey: 'snapshotCount',
        header: () => t('portfolio.columns.snapshots'),
        cell: ({ row }) => (
          <StackedCell
            primary={formatInteger(row.original.snapshotCount)}
            secondary={row.original.latestPeriod ?? undefined}
          />
        ),
        size: 124,
        enableSorting: true,
        meta: { align: 'right' },
      },
      {
        id: 'transactions',
        accessorKey: 'transactionCount',
        header: () => t('portfolio.columns.transactions'),
        cell: ({ row }) => (
          <StackedCell
            primary={formatInteger(row.original.transactionCount)}
            secondary={
              row.original.transactionCount > 0 && row.original.transactionFlowTotal !== null
                ? formatCurrency(row.original.transactionFlowTotal)
                : undefined
            }
          />
        ),
        size: 124,
        enableSorting: true,
        meta: { align: 'right' },
      },
      {
        id: 'readiness',
        header: () => t('portfolio.columns.readiness'),
        cell: ({ row }) => <ReadinessPips readiness={row.original.readiness} />,
        size: 132,
        enableSorting: false,
        meta: { align: 'center' },
      },
    ],
    [t],
  );
}
